import React, { memo } from "react";
import arrow from "../../assets/typcn_arrow-back-outline.svg";
import { loadedImages } from "./LazyImage";

const FONT = "'Plus Jakarta Sans', sans-serif";

const COLORS = {
  ink: "#23272f",
  muted: "#7c8593",
  line: "#eceef0",
  card: "#ffffff",
  cta: "#557c2e",
};

const currencyFormatter = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const CartItem = ({
  item,
  onIncrease,
  onDecrease,
  onRemove,
  onToggleWishlist,
  onShowBoxModal,
  onOpenProduct,
  isInWishlist,
  isWishlistPending = false,
  isMobile = true,
}) => {
  const imgSrc = item?.productImage || item?.boxImage;
  const isLoadedInSession = loadedImages.has(imgSrc);
  const isBox = !!item?.boxImage;

  const openItem = () => {
    if (isBox) onShowBoxModal?.(item);
    else onOpenProduct?.(item);
  };

  const price = item?.price || item?.amount || 0;
  const discountPrice = item?.discountPrice;

  const displayName = item?.productName
    ? `${item.productName}${item?.size ? ` - ${item.size}` : ""}`
    : item?.name || "Unknown Item";

  const id = item?.id || item?._id;

  const linkStyle = {
    fontSize: "12.5px",
    fontWeight: 600,
    color: COLORS.muted,
    fontFamily: FONT,
    cursor: "pointer",
    marginBottom: 0,
  };

  const stepBtn = isMobile ? 28 : 30;
  const stepperBtnStyle = {
    width: `${stepBtn}px`,
    height: `${stepBtn}px`,
    background: "#fff",
    border: "none",
    fontSize: isMobile ? "16px" : "16px",
    color: COLORS.ink,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  };

  const thumb = (
    <img
      src={imgSrc}
      onClick={openItem}
      onLoad={() => {
        if (imgSrc) loadedImages.add(imgSrc);
      }}
      style={{
        width: isMobile ? "54px" : "62px",
        height: isMobile ? "54px" : "62px",
        borderRadius: isMobile ? "11px" : "11px",
        objectFit: "cover",
        flex: "none",
        cursor: "pointer",
        transition: isLoadedInSession ? "none" : "opacity 0.2s",
      }}
      alt={displayName}
      loading="eager"
      fetchpriority="high"
    />
  );

  const priceBlock = (
    <div className="d-flex align-items-center gap-2">
      <span
        style={{
          fontSize: isMobile ? "13px" : "14px",
          fontWeight: 800,
          color: COLORS.ink,
          fontFamily: FONT,
        }}
      >
        {currencyFormatter.format(discountPrice || price)}
      </span>
      {discountPrice && (
        <span
          style={{
            fontSize: "12px",
            fontWeight: 600,
            color: "#1D293999",
            textDecoration: "line-through",
            fontFamily: FONT,
          }}
        >
          {currencyFormatter.format(price)}
        </span>
      )}
    </div>
  );

  const lookInside = isBox && (
    <span
      onClick={() => onShowBoxModal(item)}
      className="d-inline-flex align-items-center gap-1 mt-2"
      style={{
        color: COLORS.cta,
        backgroundColor: "#eef4e7",
        borderRadius: "8px",
        fontWeight: 700,
        fontSize: "11px",
        padding: "4px 9px",
        cursor: "pointer",
        fontFamily: FONT,
      }}
    >
      <img src={arrow} alt="" /> Look inside
    </span>
  );

  const links = (
    <div
      className="d-flex align-items-center"
      style={{ gap: isMobile ? "16px" : "14px" }}
    >
      <div
        onClick={() => onRemove(id)}
        className="d-flex align-items-center gap-1"
        style={linkStyle}
      >
        <i className="bi bi-trash" style={{ fontSize: "0.95rem" }}></i>
        Remove
      </div>
      <div
        onClick={() => !isWishlistPending && onToggleWishlist(id)}
        className="d-flex align-items-center gap-1"
        style={{
          ...linkStyle,
          color: isInWishlist ? COLORS.cta : COLORS.muted,
          opacity: isWishlistPending ? 0.7 : 1,
        }}
      >
        {isWishlistPending ? (
          <span
            className="spinner-border"
            role="status"
            style={{
              width: "0.85rem",
              height: "0.85rem",
              borderWidth: "2px",
              color: COLORS.cta,
            }}
          />
        ) : (
          <i
            className={`bi ${isInWishlist ? "bi-heart-fill" : "bi-heart"}`}
            style={{ fontSize: "0.9rem" }}
          ></i>
        )}
        {isInWishlist ? "Saved" : "Save"}
      </div>
    </div>
  );

  const stepper = (
    <div
      className="d-flex align-items-center"
      style={{
        border: "1px solid #e3e6ea",
        borderRadius: isMobile ? "10px" : "9px",
        overflow: "hidden",
      }}
    >
      <button onClick={() => onDecrease(id)} style={stepperBtnStyle}>
        −
      </button>
      <span
        style={{
          width: "30px",
          textAlign: "center",
          fontSize: isMobile ? "14px" : "13px",
          fontWeight: 700,
          color: COLORS.ink,
          fontFamily: FONT,
        }}
      >
        {item.quantity}
      </span>
      <button onClick={() => onIncrease(id)} style={stepperBtnStyle}>
        +
      </button>
    </div>
  );

  const name = (
    <div
      onClick={openItem}
      style={{
        fontSize: isMobile ? "13px" : "14px",
        fontWeight: 700,
        lineHeight: 1.3,
        color: COLORS.ink,
        fontFamily: FONT,
        cursor: "pointer",
      }}
    >
      {displayName}
    </div>
  );

  if (!isMobile) {
    return (
      <div
        className="d-flex"
        style={{
          gap: "14px",
          padding: "16px 0",
          borderBottom: `1px solid ${COLORS.line}`,
        }}
      >
        {thumb}
        <div style={{ flex: 1, minWidth: 0 }}>
          {name}
          <div className="mt-1">{priceBlock}</div>
          {lookInside}
          <div
            className="d-flex align-items-center justify-content-between"
            style={{ marginTop: "10px" }}
          >
            {links}
            {stepper}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.line}`,
        borderRadius: "14px",
        padding: "10px",
        marginBottom: "8px",
      }}
    >
      <div className="d-flex" style={{ gap: "10px" }}>
        {thumb}
        <div style={{ flex: 1, minWidth: 0 }}>
          {name}
          <div className="mt-1">{priceBlock}</div>
          {lookInside}
        </div>
      </div>

      <div
        className="d-flex align-items-center justify-content-between"
        style={{
          marginTop: "8px",
          paddingTop: "8px",
          borderTop: `1px solid ${COLORS.line}`,
        }}
      >
        {links}
        {stepper}
      </div>
    </div>
  );
};

export default memo(CartItem, (prev, next) => {
  return (
    prev.item?.id === next.item?.id &&
    prev.item?.quantity === next.item?.quantity &&
    prev.item?.price === next.item?.price &&
    prev.isInWishlist === next.isInWishlist &&
    prev.isWishlistPending === next.isWishlistPending &&
    prev.isMobile === next.isMobile
  );
});
